import { useEffect, useMemo, useRef, useState } from 'react'
import { Bot, Copy, MessageCircle, Send, Trash2, User, X, Minimize2, Maximize2, Loader2 } from 'lucide-react'
import { findAnswer } from '@/lib/chatbotLogic'
import { fetchList } from '@/lib/api'

const WHATSAPP_URL = 'https://wa.me/251979235206'
const TELEGRAM_URL = 'https://t.me/fortinotech'

function WhatsAppIcon({ className = 'w-5 h-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04 0C5.5 0 .16 5.33.16 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.35-1.66a11.9 11.9 0 0 0 5.68 1.45h.01c6.54 0 11.88-5.33 11.88-11.86 0-3.17-1.24-6.15-3.4-8.45ZM12.04 21.54h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.77.99 1.01-3.67-.24-.38a9.84 9.84 0 0 1-1.51-5.26c0-5.44 4.43-9.86 9.89-9.86 2.64 0 5.12 1.03 6.98 2.9a9.8 9.8 0 0 1 2.9 6.97c0 5.44-4.44 9.86-9.9 9.86Zm5.42-7.39c-.3-.15-1.76-.87-2.03-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.95 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5s1.07 2.9 1.22 3.1c.15.2 2.11 3.22 5.12 4.51.72.31 1.27.5 1.71.64.72.23 1.37.2 1.88.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
    </svg>
  )
}

function TelegramIcon({ className = 'w-5 h-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M11.94 0C5.34 0 0 5.34 0 11.94c0 6.6 5.34 11.94 11.94 11.94 6.6 0 11.94-5.34 11.94-11.94C23.88 5.34 18.54 0 11.94 0Zm5.9 8.16-1.97 9.28c-.15.66-.53.82-1.08.51l-3-2.21-1.45 1.39c-.16.16-.3.3-.61.3l.22-3.06 5.56-5.02c.24-.22-.05-.33-.38-.12l-6.87 4.33-2.96-.92c-.64-.2-.65-.64.14-.95l11.57-4.46c.54-.2 1.01.12.83.93Z" />
    </svg>
  )
}

function LiveChatButtons({ compact = false, language }) {
  return (
    <div className={compact ? 'grid grid-cols-2 gap-2' : 'flex flex-col items-end gap-3'}>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={compact
          ? 'inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] text-white px-3 py-2.5 text-xs font-semibold hover:opacity-90'
          : 'bg-[#25D366] text-white p-3.5 rounded-full shadow-xl hover:scale-110 transition'}
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon className={compact ? 'w-4 h-4' : 'w-6 h-6'} />
        {compact ? 'WhatsApp' : null}
      </a>
      <a
        href={TELEGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={compact
          ? 'inline-flex items-center justify-center gap-2 rounded-xl bg-[#229ED9] text-white px-3 py-2.5 text-xs font-semibold hover:opacity-90'
          : 'bg-[#229ED9] text-white p-3.5 rounded-full shadow-xl hover:scale-110 transition'}
        aria-label="Chat on Telegram"
      >
        <TelegramIcon className={compact ? 'w-4 h-4' : 'w-6 h-6'} />
        {compact ? 'Telegram' : null}
      </a>
    </div>
  )
}

export default function ChatbotPopup({ language }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [faqs, setFaqs] = useState([])
  const endRef = useRef(null)
  const inputRef = useRef(null)

  const welcome = language === 'am'
    ? 'ሰላም! እኔ የፎርቲኖ AI ረዳት ነኝ። ስለ ድረ-ገጽ፣ CCTV፣ ሶፍትዌር እና ሌሎች አገልግሎቶቻችን ይጠይቁ። ከሰው ጋር ለመነጋገር WhatsApp ወይም Telegram ይጠቀሙ።'
    : 'Hello! I am the Fortino AI Assistant. Ask me about websites, CCTV, software, and our other services. For live chat with our team, use WhatsApp or Telegram.'

  useEffect(() => {
    fetchList(`/faqs?lang=${language === 'am' ? 'am' : 'en'}`)
      .then(setFaqs)
      .catch(() => setFaqs([]))
  }, [language])

  useEffect(() => {
    setMessages([{ id: Date.now(), sender: 'bot', text: welcome, timestamp: new Date() }])
  }, [language])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping, isOpen])

  const suggestions = useMemo(() => (
    language === 'am'
      ? ['ቢሮአችሁ የት ነው?', 'የCCTV ካሜራ ትከላላችሁ?', 'ድረ-ገጽ ስንት ጊዜ ይወስዳል?', 'በWhatsApp ልጻፍ?']
      : ['Where is your office?', 'Do you install CCTV cameras?', 'How long does a website take?', 'How can I live chat?']
  ), [language])

  const sendMessage = (text) => {
    const value = String(text || '').trim()
    if (!value || isTyping) return

    setMessages((prev) => [...prev, { id: Date.now(), sender: 'user', text: value, timestamp: new Date() }])
    setInputValue('')
    setIsTyping(true)

    setTimeout(() => {
      const fallback = findAnswer(value, language === 'am' ? 'am' : 'en')
      const fromDb = fallback.matched ? null : matchFaqs(value, faqs)
      const result = fallback.matched ? fallback : (fromDb || fallback)
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        sender: 'bot',
        text: result?.answer || (language === 'am'
          ? 'ይህን ጥያቄ አሁን መመለስ አልቻልኩም። በWhatsApp (+251 979 235 206) ወይም Telegram (@fortinotech) በቀጥታ ያግኙን።'
          : 'I could not answer that yet. Please message us on WhatsApp (+251 979 235 206) or Telegram (@fortinotech) for live help.'),
        timestamp: new Date(),
      }])
      setIsTyping(false)
    }, 500)
  }

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        <LiveChatButtons language={language} />
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-primary to-secondary text-white p-4 rounded-full shadow-2xl hover:scale-110 transition"
          aria-label="Open Fortino AI Assistant"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      </div>
    )
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 ${isMinimized ? 'w-auto' : 'w-[420px] max-w-[calc(100vw-2rem)]'}`}>
      <div className={`bg-white dark:bg-gray-950 rounded-2xl shadow-2xl border border-border overflow-hidden flex flex-col ${isMinimized ? 'h-auto' : 'h-[620px] max-h-[calc(100vh-3rem)]'}`}>
        <div className="bg-gradient-to-r from-primary to-secondary px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl"><Bot className="w-5 h-5 text-white" /></div>
            <div>
              <h3 className="font-bold text-white">Fortino AI Assistant</h3>
              <p className="text-[10px] text-white/80 uppercase tracking-wider">
                {language === 'am' ? 'AI እና ቀጥታ ውይይት' : 'AI + live chat'}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => setIsMinimized(!isMinimized)} className="text-white/80 hover:text-white p-2">
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white p-2">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <button
                onClick={() => setMessages([{ id: Date.now(), sender: 'bot', text: welcome, timestamp: new Date() }])}
                className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                <Trash2 className="w-4 h-4" /> {language === 'am' ? 'አጥፋ' : 'Clear'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                  {message.sender === 'bot' && (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm ${message.sender === 'user' ? 'bg-primary text-white rounded-br-none' : 'bg-muted text-foreground rounded-bl-none'}`}>
                    <p className="whitespace-pre-wrap">{message.text}</p>
                    {message.sender === 'bot' && (
                      <button
                        onClick={() => navigator.clipboard.writeText(message.text)}
                        className="mt-2 opacity-50 hover:opacity-100"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {message.sender === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="text-xs text-muted-foreground">{language === 'am' ? 'እየጻፍኩ ነው...' : 'Assistant is typing...'}</div>
              )}
              <div ref={endRef} />
            </div>

            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {suggestions.map((item) => (
                <button
                  key={item}
                  onClick={() => sendMessage(item)}
                  className="text-[11px] px-3 py-1 rounded-full border border-border hover:border-primary hover:text-primary"
                >
                  {item}
                </button>
              ))}
            </div>

            <div className="px-4 pb-3">
              <p className="text-[11px] text-muted-foreground mb-2">
                {language === 'am' ? 'ከቡድናችን ጋር በቀጥታ ይነጋገሩ' : 'Live chat with our team'}
              </p>
              <LiveChatButtons compact language={language} />
            </div>

            <form
              className="p-4 border-t border-border flex gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                sendMessage(inputValue)
              }}
            >
              <input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={language === 'am' ? 'ጥያቄዎን ይጻፉ...' : 'Type your question...'}
                className="flex-1 px-4 py-3 rounded-xl border border-border bg-background text-sm"
              />
              <button type="submit" disabled={isTyping || !inputValue.trim()} className="px-4 rounded-xl bg-primary text-white disabled:opacity-40">
                {isTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
